/*******************************************************************************
Hawaiian Macadamia Nut Services
12.31.18
Harvest Utilities
Calculation functions used in the mechHarvest module
*******************************************************************************/
import { 
  packageDashForm,
  getSelectedValue,
  findEmployeeByName
} from './utils';
import { seasonForLocation } from './mech_harvest_calc';
const Materialize = window.Materialize;

/* Calculates acres per hour */
export function acresPerHour(hours, acres) {
  if (!(acres && hours)) return 0.00;
  return (Number(acres) / Number(hours)).toFixed(2);
}
/* Calculates lbs/tree or density */
export function density(weight, treeCount) {
  if (!(weight && treeCount)) return 0.00;
  return (Number(weight) / Number(treeCount)).toFixed(2);
}
/* Calculates Estimated total lbs*/
export function estimatedLbs(lbs, containerCount) {
  if (!(lbs && containerCount)) return 0.00;
  return (Number(lbs) * Number(containerCount)).toFixed(2);
}
/* Moved to mech_harvest_calc.js (testable without a browser); re-exported for existing callers */
export { acresCompleted, acresBalance } from './mech_harvest_calc';
/* Calculates the percentage of acres completed */
export function percentComplete(acresCompleted, totalAcres) {
  if (!(acresCompleted && totalAcres)) return 0.00;
  return (acresCompleted / totalAcres).toFixed(2);
}
/* Sums a selected property of an array */
export function sumProp(prop, arr) {
  if (!(prop && arr)) return 0.00;
  let total = arr.reduce((acc, cur) => { return acc + Number(cur[prop]) }, 0);
  return total.toFixed(2);
}
/* Averages a selected property of an array */
export function avgProp(prop, arr) {
  if (!(prop && arr)) return 0.00;
  let total = arr.reduce((acc, cur) => { return acc + Number(cur[prop]) }, 0);
  return (total / arr.length).toFixed(2);
}
/* Handles the start of an interval: Time between field harvests after completion */
export function handleInterval(previousHarvests) {
  // If no other harvest exist for the round and season of the selected field, start interval on 1(in days)
  if (previousHarvests.length === 0) return 1.0;
  // The intervals get updated daily in another script; if other harvests exist and the intervals have been updated
  // Set the new harvest record to the existing intervals for a given field
  return previousHarvests[0].intervals;
}
/* Displays the balance of remaining acres */
export function handleBalance(previousHarvest, selectedField) {
  if (!(previousHarvest && selectedField)) return 0.0;
  let existingAcres = previousHarvest.reduce((acc, cur) => { return acc + cur.acresCompleted; }, 0);

  return (selectedField.mechHarvest - existingAcres).toFixed(2);
}

export function initLineChart({ label, labels, data, backgroundColor, fill=false }) {
  return {
    labels: labels,
    datasets: [{
      data,
      backgroundColor,
      pointBorderColor: backgroundColor,
      borderColor: backgroundColor,
      pointBackgroundColor: '#000',
      label,
      fill
    }]
  };
}

export function initBubbleChart({ label, labels, data, fill=false }) {
  return {
    labels,
    datasets: [
      {
        label,
        fill,
        lineTension: 0.1,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
        borderCapStyle: 'butt',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: 'rgba(75,192,192,1)',
        pointBackgroundColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 1,
        pointHoverBackgroundColor: 'rgba(75,192,192,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 1,
        pointRadius: 1,
        pointHitRadius: 1,
        data
      }
    ]
  };
}

export function initBarChart({ label, labels, data, backgroundColor, borderColor, borderWidth, hoverBackgroundColor,
  hoverBorderColor, fill=false }) {
  return {
    labels: labels,
    datasets: [{
      data,
      backgroundColor,
      label,
      fill,
      borderColor,
      borderWidth,
      hoverBackgroundColor,
      hoverBorderColor
    }]
  };
}

export function initPolarChart({ label, labels, data, backgroundColor }) {
  return {
    labels: labels,
    datasets: [{
      data,
      backgroundColor,
      label
    }]
  };
}

export function initPieChart({ label, labels, data, backgroundColor }) {
  return {
    labels: labels,
    datasets: [{
      data,
      backgroundColor,
      label
    }]
  };
}

export function initDoughnutChart({label, labels, data, backgroundColor}) {
  return {
    labels: labels,
    datasets: [{
      data,
      backgroundColor,
      label
    }]
  };
}

/**
* formatMechHarvestKey(): Returns the mech harvest input label from individual harvest form
* @param {String} key: object key for mech harvest records 
* @returns {String} 
* @called-in: [right below]
**/
function formatMechHarvestKey(key) {
  if (!key) return ''
  let values = {
    "field": "Field",
    "block": "Block",
    "treesCovered": "Trees Covered",
    "hours": "Total Hours",
    "containerType": "Container Type",
    "containerCount": "Total Containers",
    "estLbsPerContainer": "Est. Lbs/Container",
    "status": "Status",
    "equipmentNo": "Equipment No.",
    "eid": "Equipment Operator"
  }
  return values[key]
}

/**
* validateMechHarvestForm(): Name says it all; verifies information is correct and 
  returns empty or full objects depending on the error; used in both create/edit
* @param {String} season: current season id(used for previous mech harvest fetches)  
* @param {Array} employees: the entire dataset of active employees
* @returns {Object} {} harvest data set or empty set
* @called-in: ['/components/harvest/create/mechHarvest_create']
**/
export function validateMechHarvestForm({ 
  seasons,
  employees,
  equipmentList
}) {
  const date = document.getElementById('mech-date').value
  const location = getSelectedValue('mech-location')
  const round = document.getElementById('mech-round').value
  const notes = document.getElementById('mech-notes').value
  const equipment = document.getElementById('mech-equipmentNo').value

  const season = seasonForLocation(seasons, location)
  if (!season) {
    Materialize.toast(`No current harvest season for location ${location}. Contact an administrator.`, 4000, 'rounded red')
    return {}
  }

  if (!(date && location && round)) {
    Materialize.toast(`A date, location and round are required to add harvest records.`, 4000, 'rounded red')
    return {}
  }
  const harvest = packageDashForm('mechHarvest-individual-form')

  if (location === '71') {
    if (harvest.field === "F525" || harvest.field ==="F520") { // Added 9.11.25 on req from P.Salmo(Harvesting Olsen orchards)
      for (let key in harvest) {
        if (harvest[key] === '' && key !== 'block') {
          if (key !== 'notes' || key !== 'eid') {
            Materialize.toast(`Entire form must be filled. Missing: ${formatMechHarvestKey(key)}`, 4000, 'rounded red')
            return {} 
          }
        }
      }
    } else {
        for (let key in harvest) {
          if (harvest[key] === '') {
            if (key !== 'notes' || key !== 'eid') {
              Materialize.toast(`Entire form must be filled. Missing: ${formatMechHarvestKey(key)}`, 4000, 'rounded red')
              return {} 
            }
          }
        }  
    }
      
  } else if (location === '72') {
    for (let key in harvest) {
      if (key === 'block' || key === 'notes' || key !== 'eid') continue 
      else {
        if (harvest[key] === '') {
          Materialize.toast(`Entire form must be filled. Missing: ${formatMechHarvestKey(key)}`, 4000, 'rounded red')
          return {}
        }
      }
    }   
  }
  
  const emp = findEmployeeByName(harvest.eid, employees)

  if (Object.keys(emp).length === 0) {
    Materialize.toast(`The following employee does not exist: ${harvest.eid}. Please check spelling and try again.`, 4000, 'rounded red')
    return {}
  }
  if (equipment === '') {
    Materialize.toast('Please select the equipment from the autocomplete list', 4000, 'rounded red');
    return {};
  }
  let equip = equipmentList.find((e) => `${e.equipmentNo} - ${e.description}` === equipment)
  if (!equip) {
    Materialize.toast('Please select the equipment from the autocomplete list', 4000, 'rounded red');
    return {};
  }
  
  return {
    ...harvest,
    date,
    location,
    round,
    notes,
    intervals: 1.0,
    seasonId: season.id,
    eid: emp.eid
  }
}




























/* END */



// WEBPACK FOOTER //
// ./src/harvest_utils.js